import React from "react";

import {
	ConfirmModal,
	Table,
	Badge,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import {
	useInfiniteQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";

import ReportsServices from "@/services/ReportsService";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useReportDownload } from "@/hooks/useReportDownload";

import { PlanFeatureBanner } from "@/components/organisms/PlanGate/PlanFeatureBanner";
import { PageLayout } from "@/components/templates";

import { reportKeys } from "@/constants/CQueryKeys";

import { TSavedReport } from "@/types";

import { promiseToast } from "@/modules/index";

export const ReportsView: React.FC = () => {
	const navigate = useNavigate();
	const invoicesOn = useFeatureFlag("invoices");
	const gate = usePlanGate("pdf");
	const queryClient = useQueryClient();
	const [deleteTarget, setDeleteTarget] = React.useState<TSavedReport | null>(
		null
	);

	const { isLoading, data, fetchNextPage, hasNextPage } = useInfiniteQuery({
		queryKey: reportKeys.saved,
		queryFn: ({ pageParam }) =>
			ReportsServices.list({ page: pageParam, limit: 20 }),
		getNextPageParam: (lastPage, pages) => {
			const next = pages.length + 1;
			return next <= lastPage.pages ? next : undefined;
		},
		initialPageParam: 1,
	});

	const items = data?.pages.flatMap((p) => p.data) ?? [];

	const deleteMutation = useMutation({
		mutationFn: (id: number) => ReportsServices.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: reportKeys.saved });
		},
	});

	const { download } = useReportDownload();

	const handleDelete = (item: TSavedReport) => {
		setDeleteTarget(item);
	};

	const handleGenerate = (item: TSavedReport) => {
		download(item.id);
	};

	const columns: TTableColumn<TSavedReport>[] = [
		{
			key: "contextMenu",
		},
		{
			key: "name",
			label: t("reports.columns.name"),
			sortable: true,
			render: (item) => (
				<div className="flex items-center gap-2">
					{item.name}
					{item.isDraft && (
						<Badge color="neutral" size="sm">
							{t("reports.draft")}
						</Badge>
					)}
				</div>
			),
			width: "30%",
		},
		{
			key: "client",
			label: t("client.label"),
			render: (item) =>
				item.client ? (
					<div className="flex items-center gap-2">
						{item.client.logo && (
							<img
								src={item.client.logo}
								alt={item.client.name}
								className="h-6 w-6 object-contain"
							/>
						)}
						<span className="text-sm">{item.client.name}</span>
					</div>
				) : (
					<span className="text-sm text-neutral-400">—</span>
				),
		},
		{
			key: "dateFrom",
			label: t("reports.columns.period"),
			render: (item) => (
				<span className="text-sm text-neutral-600">
					{`${format(new Date(item.dateFrom), "dd/MM/yyyy")} → ${format(new Date(item.dateTo), "dd/MM/yyyy")}`}
				</span>
			),
		},
		{
			key: "workType",
			label: t("workTypes.label"),
			render: (item) =>
				item.workType ? (
					<span className="text-sm capitalize">
						{t(`workTypes.types.${item.workType}`)}
					</span>
				) : (
					"-"
				),
		},
		{
			key: "createdAt",
			label: t("reports.columns.createdAt"),
			sortable: true,
			render: (item) => (
				<span className="text-sm text-neutral-500">
					{format(new Date(item.createdAt), "dd/MM/yyyy")}
				</span>
			),
		},
	];

	return (
		<>
			<PageLayout
				title={t("reports.title")}
				isLoading={isLoading}
				buttons={[
					// Locked off-plan: opens the upgrade modal. The API refuses regardless.
					gate.button({
						label: t("reports.new"),
						color: "primary",
						onClick: () => navigate("/reports/new"),
					}),
				]}
			>
				<PlanFeatureBanner on="pdf" className="mb-4" />
				<Table
					isLoading={isLoading}
					items={items}
					getItemKey={(item) => item.id}
					onRowClick={(item) => navigate(`/reports/${item.id}`)}
					onLoadMore={fetchNextPage}
					hasMore={hasNextPage}
					columns={columns}
					rowActions={[
						{
							label: t("reports.generateCTA"),
							onClick: handleGenerate,
							picto: "download",
						},
						// Invoices paused (feature_flags): no way in from here.
						...(invoicesOn
							? [
									{
										label: t("invoices.actions.invoice"),
										onClick: (item: TSavedReport) => {
											if (item.isDraft) return;
											navigate(
												`/invoices/new?reportId=${item.id}`
											);
										},
										picto: "fileText" as const,
									},
								]
							: []),
						{
							label: t("actions.edit"),
							onClick: (item: TSavedReport) =>
								navigate(`/reports/${item.id}`),
							picto: "pen",
						},
						{
							label: t("actions.delete"),
							onClick: handleDelete,
							picto: "trash",
						},
					]}
				/>
			</PageLayout>
			<ConfirmModal
				open={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				size="sm"
				title={t("reports.deleteModal.title")}
				onConfirm={() => {
					promiseToast(deleteMutation.mutateAsync(deleteTarget!.id));
					setDeleteTarget(null);
				}}
				confirmText={t("actions.delete")}
				cancelText={t("actions.cancel")}
			/>
		</>
	);
};
